
import get from 'lodash/get';
import { extractBrowse } from '@/util/global';

export default {
  props: {
    options: {
      type: Array,
      default: () => [],
    },
    id: {
      type: String,
      required: true,
    },
    slug: {
      type: String,
      required: false,
      default: 'group',
    },
  },
  data() {
    const qs = this.$route?.query || {};
    const params = extractBrowse(this.$route?.params.pathMatch);

    return {
      highlightOptionIndex: -1,
      originalInput: '',
      searchInput: '',
      formattedOptions: [],
      type: qs[this.slug] || params[this.slug] || null,
      isTypeOpen: false,
    };
  },
  computed: {
    types() {
      const attrib = this.$store.state.browse?.attributes?.find((a) => a.slug === this.slug);

      return attrib?.type_options?.options || [];
    },
    selectedType() {
      const attrib = this.$store.state.browse?.attributes?.find((a) => a.slug === this.slug);

      if (!this.type || !attrib) {
        return 'All';
      }

      const option = attrib.type_options.options.find((o) => o.value === this.type);
      if (option) {
        return option.name;
      }

      return 'All';
    },
  },
  watch: {
    '$route.query': function () {
      const params = extractBrowse(this.$route?.params.pathMatch);

      if (this.slug === 'group' && this.$route.query.group) {
        this.type = this.$route.query.group;
      } else if (this.slug === 'group' && params.group) {
        this.type = params.group;
      } else if (this.$route.query.product_type) {
        this.type = this.$route.query.product_type;
      } else {
        this.type = null;
      }

      if (this.$route.query.query !== this.searchInput) {
        this.searchInput = this.$route.query.query;
        this.$store.commit('setAutocompleteQuery', this.searchInput);
      }
    },
    type() {
      if (this.slug === 'group') {
        this.$store.commit('setAutocompleteGroup', this.type);
      } else {
        this.$store.commit('setAutocompleteProductType', this.type);
      }

      this.rewriteFormAction();
    },
  },
  created() {
    this.hideOverlayTimeoutId = null;
    this.rewriteFormAction();
  },
  mounted() {
    const searchQuery = get(this, ['$route', 'query', 'query'], '').trim();
    if (searchQuery && searchQuery.length) {
      this.searchInput = searchQuery;
      this.originalInput = searchQuery;
      // Without setSearchQuery set, cannot clear results properly after page load
      this.$store.commit('setAutocompleteQuery', this.searchInput);
    }

    if (this.type) {
      if (this.slug === 'group') {
        this.$store.commit('setAutocompleteGroup', this.type);
      } else {
        this.$store.commit('setAutocompleteProductType', this.type);
      }
      this.rewriteFormAction();
    }

    // Set the initial window width
    this.currentWindowWidth = window.innerWidth;

    // Set an event listener to know when the window resizes to
    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize);
    });
  },
  beforeUnmount() {
    if (this.hideOverlayTimeoutId) {
      // clear the hide timeout if one was set. No-op if one is not set.
      clearTimeout(this.hideOverlayTimeoutId);

      // toggle the overlay off with no timeout when unmounting the component
      this.toggleOverlayVisibility(false);
    }

    // Remove the resize event listener
    window.removeEventListener('resize', this.onResize);
    document.body.removeEventListener('touchmove', this.preventTouchMove, false);
  },
  methods: {
    rewriteFormAction() {
      if (this.$refs && this.$refs.searchForm) {
        if (this.type) {
          this.$refs.searchForm.action = `/${this.type}`;
        } else {
          this.$refs.searchForm.action = '/search';
        }
      }
    },
    toggleType() {
      this.setIsTypeOpen(!this.isTypeOpen);
    },
    setType(type) {
      this.type = type;
      this.setIsTypeOpen(false);
      this.$nextTick(() => this.$refs.input.focus());
      // backup incase above fails
      setTimeout(() => this.$refs.input.focus(), 10);
    },
    setIsTypeOpen(open) {
      this.isTypeOpen = open;
      if (open) {
        this.showOverlay();
      } else {
        this.hideOverlay();
      }
    },
    isInputEmpty() {
      return !(this.searchInput && this.searchInput.length);
    },
    addActivity() {
      const userId = get(this, ['$store', 'state', 'session', 'user', 'id']);

      if (this.searchInput && userId && this.$activity) {
        this.$activity.add({
          actor: `User:${userId}`,
          verb: 'deal_search',
          full_text: this.searchInput,
        });
      }
    },
    onClickSearchOption(option) {
      if (option.slug) {
        window.location = `/products/${option.slug}?query=${encodeURIComponent(this.searchInput || option.value)}`;
        return;
      }

      this.searchInput = option.value;

      this.$nextTick(() => {
        const searchForm = get(this, ['$refs', 'searchForm']);

        if (searchForm?.submit) {
          searchForm.submit();
        }
      });
    },
    onKeyDown(e) {
      const {
        highlightOptionIndex: optIndex,
        options,
        originalInput,
      } = this;

      if (!options.length) return;

      if (e.keyCode === 40) {
        this.highlightOptionIndex = (optIndex < options.length)
          ? optIndex + 1
          : 0;

        const newOption = options[this.highlightOptionIndex]?.public_name;

        if (newOption) {
          this.searchInput = newOption;
        } else {
          this.searchInput = originalInput;
        }
      } else if (e.keyCode === 38) {
        this.highlightOptionIndex = (optIndex < 1)
          ? options.length
          : optIndex - 1;

        const newOption = options[this.highlightOptionIndex]?.public_name;

        if (newOption) {
          this.searchInput = newOption;
        } else {
          this.searchInput = originalInput;
        }

        // Hack to get the input caret to move to the end
        this.$nextTick(() => {
          setTimeout(() => {
            e.target.setSelectionRange(255, 255);
          }, 10);
        });
      }
    },
    onClearSearch() {
      this.useAutocompleteStore('');
      this.$refs.input.focus();
    },
    useAutocompleteStore(value) {
      this.searchInput = value;

      this.originalInput = this.searchInput;
      this.$store.commit('setAutocompleteQuery', this.searchInput);
    },
    /**
     * Show an overlay when search is activated (focus or product-type dropdown)
     *
     * jw: the idea with these overlay functions is to isolate the management of the timeout
     *     function from the code that actually makes the overlay visible or not.
     */
    showOverlay() {
      // jw: clear the hide timeout if one was set. No-op if one is not set.
      clearTimeout(this.hideOverlayTimeoutId);

      // showing actions will happen immediatly with no delay, so clearing timeout
      this.hideOverlayTimeoutId = null;

      // Call to actually toggle the overlay
      this.toggleOverlayVisibility(true);
    },
    /**
     * Remove an overlay when search is deactivated
     */
    hideOverlay() {
      // jw: clear the hide timeout if one was set. No-op if one is not set.
      clearTimeout(this.hideOverlayTimeoutId);

      // hiding actions will happen on a 150ms delay to account for the delay between
      // search focus and opening the product-type dropdown that clears focus
      this.hideOverlayTimeoutId = setTimeout(
        () => this.toggleOverlayVisibility(false),
        150,
      );
    },
    /**
     * Toggle the overlay visibility on/off
     * @param {boolean} show  whether to show or hide the overlay
     */
    toggleOverlayVisibility(show) {
      // jw: first, let's get the overlay and ensure we were able to find it.
      const overlay = document.getElementById('sumo-main-overlay');
      if (!overlay) {
        return;
      }

      document.body.classList.toggle('overflow-hidden', show);

      // jw: next, if the overlay is already in the state we want we can short out.
      if (show === overlay.classList.contains('active')) {
        return;
      }

      // jw: finally, we can just update the classes and call it a day.
      if (show) {
        overlay.classList.add('active');

        // See method for more info. Using passive false as recommended for touch events on MDN docs
        // https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#improving_scrolling_performance_with_passive_listeners
        document.body.addEventListener('touchmove', this.preventTouchMove, { passive: false });
      } else {
        const newNavbar = document.getElementsByClassName('new-navbar');

        // dh: if new navbar is open then don't close overlay
        if (newNavbar && newNavbar.length > 0 && newNavbar[0].classList.contains('nav-open')) {
          return;
        }

        overlay.classList.remove('active');
        // See method for more info
        document.body.removeEventListener('touchmove', this.preventTouchMove, false);
      }
    },
    /**
     * Prevent scrolling for mobile using only CSS doesn not work on iOS with Safari,
     * so we disable the touchmove to prevent scrolling while having the overlay enable
     * https://hynchrstn.wordpress.com/2021/01/12/disable-ios-scrolling-overflow-hidden-is-not-working/
     */
    preventTouchMove(event) {
      event.preventDefault();
    },
    /**
     * Callback for when the window is resized
     * @param {boolean} show  whether to show or hide the overlay
     */
    onResize() {
      // If the product-type dropdown is not open, we don't need to do any checks
      if (!this.isTypeOpen) {
        // Update the currentWidth
        this.currentWindowWidth = window.innerWidth;
        return;
      }

      // Need to determine if we need to close the dropdown (and therefore the overlay)
      const mobileCutoffSize = 991.98; // from sumo-navbar.css
      const newCurrentWindowWidth = window.innerWidth;

      // Determine if the width was/is mobile/small
      const wasMobile = this.currentWindowWidth < mobileCutoffSize;
      const isMobile = newCurrentWindowWidth < mobileCutoffSize;

      // if the viewport type changed we need to close the dropdown and
      // remove the overlay
      if (wasMobile !== isMobile) {
        // toggle the product-type setting, which we know, from above, is true to false
        this.toggleType();

        // will also bypass the delay for removing the overlay since we know it
        // will not be needed
        this.toggleOverlayVisibility(false);
      }

      // Update the currentWidth
      this.currentWindowWidth = newCurrentWindowWidth;
    },
  },
};
